import { useGiveawayTypes } from '@/hooks/queries'
import { backgroundImage } from '@/lib/format'
import { entryOptionLabel, isFirstEntries } from '@/models/giveaway'
import { cn } from '@/lib/cn'
import { EntriesStrip } from '../giveaway/EntriesStrip'
import { ReferralIcon } from '../giveaway/ReferralIcon'
import type { StepProps } from './types'
import { LinkedText } from '../ui/LinkedText'

interface ReviewStepProps extends Omit<StepProps, 'onNext' | 'update'> {
  onComplete: () => void
  isSubmitting: boolean
}

/** Port of components/new_giveaway/pages/complete.vue */
export function ReviewStep({ giveaway, onBack, onComplete, isSubmitting }: ReviewStepProps) {
  const cover = giveaway.cover_media?.src ?? giveaway.display_cover.src
  const { data: giveawayTypes } = useGiveawayTypes()
  const firstEntries = isFirstEntries(giveaway, giveawayTypes)

  return (
    <div className="content-section">
      <div
        className={cn('cover-media-row background-cover with-margin', cover && 'filled')}
        style={backgroundImage(cover)}
      />

      <EntriesStrip giveaway={giveaway} className="with-margin" />

      {firstEntries && giveaway.require_all_objectives && giveaway.giveaway_objective.length > 1 ? (
        <div className="content-row small upper with-margin">
          Every entry option is required to claim a key
        </div>
      ) : null}

      {giveaway.giveaway_objective.map((objective) => (
        <div className="field-row with-margin" key={objective.key}>
          <div className="field">
            <div className="field-box">
              <div className="field-box-prepend">
                {objective.is_referral ? <ReferralIcon /> : <img src="/icons/discord.png" alt="" />}
              </div>
              <div className="field-box-content upper small-text">{objective.display_name}</div>
              <div className="field-box-append">
                {entryOptionLabel(objective, firstEntries, giveaway.require_all_objectives)}
              </div>
            </div>
          </div>
        </div>
      ))}

      {giveaway.description ? (
        <>
          <div className="content-row left with-margin small-margin upper">Description</div>
          <div className="content-row with-margin description-text left">
            <LinkedText text={giveaway.description} />
          </div>
        </>
      ) : null}

      <div className="content-row upper with-margin highlight">
        This is how your new giveaway will appear to other users
      </div>

      <div className="footer">
        <div className="cta-row">
          <button type="button" className="cta" onClick={onBack}>
            Back
          </button>
          <button type="button" className="cta next" onClick={onComplete} disabled={isSubmitting}>
            {isSubmitting ? <span className="spinner-inline" aria-hidden="true" /> : null}
            {isSubmitting ? 'Creating…' : 'Complete'}
          </button>
        </div>
      </div>
    </div>
  )
}
